import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AchievementPopup } from 'components/AchievementPopup';
import { useAuth } from 'context/AuthContext';
import { fetchChallengesWithStatus } from 'services/challenge.service';
import { invalidateCache } from 'services/cache';
import { filterChallengesByJourneyProfile } from 'pages/HomeElbb/utils/journeyChallenges';

interface AchievementItem {
  id: string;
  title: string;
  description?: string;
  image?: string;
}

interface QueueItem extends AchievementItem {
  position: number;
  total: number;
}

interface AchievementFeedbackProviderProps {
  children: ReactNode;
}

const AUTO_CLOSE_MS = 5000;
const REFRESH_DEBOUNCE_MS = 300;
const ELBB_FEEDBACK_ROUTE_PREFIXES = [
  '/home-elbb',
  '/iniciativas-elbb',
  '/minha-jornada-elbb',
  '/minhas-iniciativas-elbb',
  '/inscricao-iniciativa-elbb',
  '/listagem-inscricao-elbb',
  '/listagem',
  '/validacaoiniciativas',
  '/cadastroelbb',
  '/regulamento-elbb'
];

const isElbbFeedbackRoute = (pathname: string): boolean => {
  return ELBB_FEEDBACK_ROUTE_PREFIXES.some((routePrefix) => {
    return pathname === routePrefix || pathname.startsWith(`${routePrefix}/`);
  });
};

export function AchievementFeedbackProvider({ children }: AchievementFeedbackProviderProps) {
  const { user } = useAuth();
  const location = useLocation();

  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [currentAchievement, setCurrentAchievement] = useState<QueueItem | null>(null);

  const knownChallengeIdsRef = useRef<Set<string>>(new Set());
  const bootstrapDoneRef = useRef(false);
  const debounceTimerRef = useRef<number | null>(null);

  const journeyProfile = useMemo(() => {
    return {
      ...(user?.extra || {}),
      ...(user?.detail || {})
    };
  }, [user?.detail, user?.extra]);

  const canShowFeedback = useMemo(() => {
    const pathname = location.pathname.toLowerCase();
    const isElbbRoute = isElbbFeedbackRoute(pathname);
    const isAuthPage = pathname.includes('login');
    const isElbbConfirmationPage = pathname.includes('confirmacao-elbb');

    return isElbbRoute && !isAuthPage && !isElbbConfirmationPage;
  }, [location.pathname]);

  const mapCompletedChallenges = useCallback(async () => {
    if (!user?._id) return [] as AchievementItem[];

    const challenges = await fetchChallengesWithStatus(user._id);
    const filteredChallenges = filterChallengesByJourneyProfile(challenges, journeyProfile);

    return filteredChallenges
      .filter((challenge) => challenge.concluida)
      .map((challenge) => ({
        id: challenge.id,
        title: challenge.titulo,
        description: challenge.descricao,
        image: challenge.icone,
      }));
  }, [journeyProfile, user?._id]);

  const appendToQueue = useCallback((achievements: AchievementItem[]) => {
    if (!achievements.length) return;

    setQueue((previousQueue) => {
      const blockedIds = new Set([
        ...previousQueue.map((item) => item.id),
        ...(currentAchievement ? [currentAchievement.id] : []),
      ]);

      const freshItems = achievements.filter((item) => !blockedIds.has(item.id));
      if (!freshItems.length) {
        return previousQueue;
      }

      const total = freshItems.length;
      const preparedItems = freshItems.map((item, index) => ({
        ...item,
        position: index + 1,
        total,
      }));

      return [...previousQueue, ...preparedItems];
    });
  }, [currentAchievement]);

  const syncAchievements = useCallback(async (isBootstrap = false) => {
    if (!user?._id) return;

    try {
      invalidateCache(`player-challenges-${user._id}`);

      const completedChallenges = await mapCompletedChallenges();
      const completedIds = new Set(completedChallenges.map((item) => item.id));

      if (isBootstrap || !bootstrapDoneRef.current) {
        knownChallengeIdsRef.current = completedIds;
        bootstrapDoneRef.current = true;
        return;
      }

      const newItems = completedChallenges.filter((item) => !knownChallengeIdsRef.current.has(item.id));
      knownChallengeIdsRef.current = completedIds;

      appendToQueue(newItems);
    } catch (error) {
      console.error('Erro ao sincronizar feedback de conquistas:', error);
    }
  }, [appendToQueue, mapCompletedChallenges, user?._id]);

  const closeCurrentAchievement = useCallback(() => {
    setCurrentAchievement(null);
  }, []);

  useEffect(() => {
    if (!user?._id) {
      knownChallengeIdsRef.current = new Set();
      bootstrapDoneRef.current = false;
      setQueue([]);
      setCurrentAchievement(null);
      return;
    }

    syncAchievements(true);
  }, [syncAchievements, user?._id]);

  useEffect(() => {
    const scheduleSync = () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = window.setTimeout(() => {
        syncAchievements(false);
      }, REFRESH_DEBOUNCE_MS);
    };

    const handleActionCompleted = () => {
      scheduleSync();
    };

    window.addEventListener('action-log:completed', handleActionCompleted as EventListener);

    return () => {
      window.removeEventListener('action-log:completed', handleActionCompleted as EventListener);

      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, [syncAchievements]);

  useEffect(() => {
    if (currentAchievement || !queue.length || !canShowFeedback) return;

    const [nextAchievement, ...remainingQueue] = queue;
    setCurrentAchievement(nextAchievement);
    setQueue(remainingQueue);
  }, [canShowFeedback, currentAchievement, queue]);

  useEffect(() => {
    if (!currentAchievement || !canShowFeedback) return;

    const timer = window.setTimeout(() => {
      closeCurrentAchievement();
    }, AUTO_CLOSE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [canShowFeedback, closeCurrentAchievement, currentAchievement]);

  return (
    <>
      {children}
      {currentAchievement && canShowFeedback ? (
        <AchievementPopup
          title={currentAchievement.title}
          description={currentAchievement.description}
          image={currentAchievement.image}
          position={currentAchievement.position}
          total={currentAchievement.total}
          onClose={closeCurrentAchievement}
        />
      ) : null}
    </>
  );
}
